import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { loadCookiePreferences, saveCookiePreferences, syncPreferencesToBackend, getStoredCookiePreferences, CookiePreferences } from '../../services/cookiePreferencesService';
import { isTokenAvailable } from '../../utility/helpers';
import eventBus from '../../utility/eventBus';
import './CookieConsent.scss';

const CookieConsent: React.FC = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const [preferences, setPreferences] = useState<CookiePreferences>({
        necessary: true, // Always true, cannot be disabled
        analytics: false,
        marketing: false,
    });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let bannerTimer: ReturnType<typeof setTimeout> | undefined;

        const initializePreferences = async () => {
            setIsLoading(true);
            try {
                // Try to load preferences from backend (if authenticated) or localStorage
                const savedPrefs = await loadCookiePreferences();
                
                if (savedPrefs) {
                    // User has already made a choice
                    setPreferences(savedPrefs);
                    setShowBanner(false);
                } else {
                    // User hasn't made a choice yet, show banner after a short delay
                    bannerTimer = setTimeout(() => {
                        setShowBanner(true);
                    }, 1000);
                }
            } catch (error) {
                console.error('Error initializing cookie preferences:', error);
                const savedPrefs = getStoredCookiePreferences();
                if (savedPrefs) {
                    setPreferences(savedPrefs);
                    setShowBanner(false);
                } else {
                    setShowBanner(true);
                }
            } finally {
                setIsLoading(false);
            }
        };

        void initializePreferences();

        return () => {
            if (bannerTimer) {
                clearTimeout(bannerTimer);
            }
        };
    }, []);

    // Allow other parts of the app (e.g., Edit Profile) to open the preferences panel
    useEffect(() => {
        const handleOpenPreferences = () => {
            setShowBanner(true);
            setShowPreferences(true);
        };

        document.addEventListener('openCookiePreferences', handleOpenPreferences as EventListener);

        return () => {
            document.removeEventListener('openCookiePreferences', handleOpenPreferences as EventListener);
        };
    }, []);

    // Sync preferences with backend when user logs in or when app loads with an existing session
    useEffect(() => {
        const syncWithBackend = async () => {
            if (!isTokenAvailable()) {
                return;
            }

            const hasSynced = sessionStorage.getItem('cookiePrefsSynced');
            if (hasSynced === 'true') {
                return;
            }

            try {
                // 1) Try to pull preferences from backend (source of truth once user has any backend data)
                const backendPrefs = await loadCookiePreferences();
                if (backendPrefs) {
                    setPreferences(backendPrefs);
                    setShowBanner(false);
                    // Notify rest of app (e.g. AnalyticsWrapper) that consent is now known
                    eventBus.dispatch('cookiePreferencesChanged', backendPrefs);
                    sessionStorage.setItem('cookiePrefsSynced', 'true');
                    return;
                }

                // 2) If backend has nothing but local prefs exist (user accepted before login),
                // push localStorage → backend
                const localPrefs = getStoredCookiePreferences();
                if (localPrefs) {
                    await syncPreferencesToBackend();
                    sessionStorage.setItem('cookiePrefsSynced', 'true');
                }
            } catch (error) {
                console.warn('Failed to sync cookie preferences with backend:', error);
            }
        };

        // Run on mount in case user was already logged in
        void syncWithBackend();

        // When user logs in, sync local and backend preferences
        const handleLogin = () => {
            void syncWithBackend();
        };

        // On logout, clear sync flag so next login can sync again (e.g. different user or same user)
        const handleLogout = () => {
            sessionStorage.removeItem('cookiePrefsSynced');
        };

        eventBus.on('login', handleLogin);
        eventBus.on('logout', handleLogout);

        return () => {
            eventBus.remove('login', handleLogin);
            eventBus.remove('logout', handleLogout);
        };
    }, []);

    const handleAcceptAll = async () => {
        const allAccepted: CookiePreferences = {
            necessary: true,
            analytics: true,
            marketing: true,
        };
        await savePreferences(allAccepted);
        setShowBanner(false);
    };

    const handleRejectAll = async () => {
        const onlyNecessary: CookiePreferences = {
            necessary: true,
            analytics: false,
            marketing: false,
        };
        await savePreferences(onlyNecessary);
        setShowBanner(false);
    };

    const handleSavePreferences = async () => {
        await savePreferences(preferences);
        setShowPreferences(false);
        setShowBanner(false);
    };

    const savePreferences = async (prefs: CookiePreferences) => {
        setPreferences(prefs);
        
        // Save to backend (if authenticated) and localStorage
        await saveCookiePreferences(prefs);
        // Notify other parts of the app (e.g., AnalyticsWrapper) that consent changed
        eventBus.dispatch('cookiePreferencesChanged', prefs);
        
        // Initialize cookies based on preferences
        if (prefs.analytics) {
            // Initialize analytics cookies/tracking
            console.log('Analytics cookies enabled');
            // Example: window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
        } else {
            // Disable analytics cookies
            console.log('Analytics cookies disabled');
            // Example: window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
        }
        
        if (prefs.marketing) {
            // Initialize marketing cookies/tracking
            console.log('Marketing cookies enabled');
            // Example: window.gtag?.('consent', 'update', { ad_storage: 'granted' });
        } else {
            // Disable marketing cookies
            console.log('Marketing cookies disabled');
            // Example: window.gtag?.('consent', 'update', { ad_storage: 'denied' });
        }
    };

    const handlePreferenceChange = (type: keyof CookiePreferences) => {
        if (type === 'necessary') return; // Cannot disable necessary cookies
        setPreferences((prev) => ({
            ...prev,
            [type]: !prev[type],
        }));
    };

    // Don't render anything while loading preferences
    if (isLoading) {
        return null;
    }

    if (!showBanner) {
        return null;
    }

    return (
        <>
            {showBanner && !showPreferences && (
                <div className="cookie-consent-banner">
                    <div className="cookie-consent-content">
                        <div className="cookie-consent-text">
                            <p>
                                We use cookies to enhance your experience. 
                                <a href="/privacy"> Learn more</a>
                            </p>
                        </div>
                        <div className="cookie-consent-buttons">
                            <button 
                                className="cookie-btn-small"
                                onClick={() => setShowPreferences(true)}
                            >
                                Customize
                            </button>
                            <button 
                                className="cookie-btn-small"
                                onClick={handleRejectAll}
                            >
                                Reject All
                            </button>
                            <button 
                                className="cookie-btn-small cookie-btn-primary"
                                onClick={handleAcceptAll}
                            >
                                Accept All
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showPreferences && (
                <div className="cookie-consent-banner cookie-preferences-modal">
                    <div className="cookie-consent-content">
                        <div className="cookie-consent-header">
                            <h5>Cookie Preferences</h5>
                            <button 
                                className="cookie-close-btn" 
                                onClick={() => setShowPreferences(false)}
                                aria-label="Close preferences"
                            >
                                ×
                            </button>
                        </div>
                        <div className="cookie-preferences-content">
                            <div className="cookie-preference-item">
                                <div className="cookie-preference-info">
                                    <h6>Necessary Cookies</h6>
                                    <p>These cookies are essential for the website to function properly. They cannot be disabled.</p>
                                </div>
                                <div className="cookie-toggle">
                                    <input
                                        type="checkbox"
                                        checked={preferences.necessary}
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>
                            <div className="cookie-preference-item">
                                <div className="cookie-preference-info">
                                    <h6>Analytics Cookies</h6>
                                    <p>These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.</p>
                                </div>
                                <div className="cookie-toggle">
                                    <input
                                        type="checkbox"
                                        checked={preferences.analytics}
                                        onChange={() => handlePreferenceChange('analytics')}
                                    />
                                </div>
                            </div>
                            <div className="cookie-preference-item">
                                <div className="cookie-preference-info">
                                    <h6>Marketing Cookies</h6>
                                    <p>These cookies are used to deliver personalized advertisements and track campaign effectiveness.</p>
                                </div>
                                <div className="cookie-toggle">
                                    <input
                                        type="checkbox"
                                        checked={preferences.marketing}
                                        onChange={() => handlePreferenceChange('marketing')}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="cookie-consent-buttons">
                            <Button 
                                variant="outline-secondary" 
                                className="cookie-btn-cancel"
                                onClick={() => {
                                    setShowPreferences(false);
                                    setShowBanner(false);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="primary" 
                                className="cookie-btn-save"
                                onClick={handleSavePreferences}
                            >
                                Save Preferences
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CookieConsent;
